import {
  Table,
  type Column,
  type Row,
  type SortDescriptor,
  tableOperations,
} from '@ardoq/table';
import { GremlinPathResult, GremlinResultType } from '../types';
import { GremlinPath } from './GremlinPath';
import { dispatchAction } from '@ardoq/rxbeach';
import { showGremlinResultDetails } from '../../actions';
import { SortByFn, SortById } from '@ardoq/pagination';
import { getIn } from 'utils/collectionUtil';
import { SortOrder } from '@ardoq/api-types';
import { RESULTS_PER_PAGE } from '../consts';
import { ButtonSize, TertiaryButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { isTruthy } from '@ardoq/common-helpers';

const PATH_COLUMN_ID = 'path';
const ACTIONS_COLUMN_ID = '__actions';

type GremlinResultsTablePathProps = {
  results: GremlinPathResult[];
  printable?: boolean;
  sortBy: SortByFn;
  sortById: SortById;
  sortOrder?: SortOrder;
  i18nLocale: I18nLocale;
};

type RowData = {
  [PATH_COLUMN_ID]: { result: GremlinPathResult; printable: boolean };
  [ACTIONS_COLUMN_ID]: { result: GremlinPathResult };
};

const getLabelsFromResult = (result: GremlinPathResult) =>
  result.objects.map(object => ({
    id: object.id,
    label: object.label,
    name: getIn<string>(object, ['properties', 'name', 0, 'value']) || '',
    type: object.type,
  }));

const getPathSortValue = (result: GremlinPathResult): string =>
  getLabelsFromResult(result)
    .map(({ label, name }) => label + name)
    .join('');

const PathCell = ({ result, printable }: RowData[typeof PATH_COLUMN_ID]) => (
  <GremlinPath pathLabels={getLabelsFromResult(result)} printable={printable} />
);

const getPathValue = ({ result }: RowData[typeof PATH_COLUMN_ID]): string =>
  getPathSortValue(result);

type ShowDetailsCellProps = RowData[typeof ACTIONS_COLUMN_ID] & WithI18nLocale;

const ShowDetailsCell = ({ result, i18nLocale }: ShowDetailsCellProps) => (
  <FlexBox justify="center" height="full" align="center">
    <TertiaryButton
      buttonSize={ButtonSize.SMALL}
      onClick={() =>
        dispatchAction(
          showGremlinResultDetails({
            result,
            resultType: GremlinResultType.PATH,
          })
        )
      }
    >
      {t(i18nLocale, 'search:gremlinResultsTable.showDetails', 'Show details')}
    </TertiaryButton>
  </FlexBox>
);

const buildColumns = (
  printable: boolean,
  i18nLocale: I18nLocale
): Column<RowData>[] => {
  const columns: Column<RowData>[] = [
    {
      id: PATH_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.path', 'Path'),
      cellRenderer: PathCell,
      valueGetter: getPathValue,
    },
  ];
  if (!printable) {
    columns.push({
      id: ACTIONS_COLUMN_ID,
      title: '',
      cellRenderer: (cellData: RowData[typeof ACTIONS_COLUMN_ID]) => (
        <ShowDetailsCell {...cellData} i18nLocale={i18nLocale} />
      ),
      valueGetter: () => '',
      width: 150,
      suppressSorting: true,
      suppressHeaderContextMenu: true,
      resizable: false,
    });
  }
  return columns;
};

const toRows = (
  results: GremlinPathResult[],
  printable: boolean
): Row<RowData>[] =>
  results.filter(isTruthy).map((result, index) => {
    const pathResult = result;
    return {
      id: String(index),
      data: {
        [PATH_COLUMN_ID]: { result: pathResult, printable },
        [ACTIONS_COLUMN_ID]: { result: pathResult },
      },
    };
  });

const toSortDescriptor = (
  sortById: SortById,
  sortOrder: SortOrder | undefined
): SortDescriptor | null => {
  if (!sortById || !sortOrder) return null;
  return {
    column: String(sortById),
    direction: sortOrder === SortOrder.ASC ? 'asc' : 'desc',
  };
};

const GremlinResultsTablePath = ({
  results,
  printable = false,
  sortBy,
  sortById,
  sortOrder,
  i18nLocale,
}: GremlinResultsTablePathProps) => (
  <Table<RowData>
    aria-label={t(
      i18nLocale,
      'search:gremlinResultsTable.pathAriaLabel',
      'Gremlin path results'
    )}
    rows={toRows(results, printable)}
    columns={buildColumns(printable, i18nLocale)}
    sortable={{
      descriptor: toSortDescriptor(sortById, sortOrder),
      onChange: descriptor => {
        if (descriptor?.column === PATH_COLUMN_ID) {
          sortBy(
            (result: GremlinPathResult) => getPathSortValue(result),
            PATH_COLUMN_ID
          );
        }
      },
      externallySorted: true,
    }}
    height={`${tableOperations.getDynamicTableHeight(results.length, RESULTS_PER_PAGE)}px`}
  />
);

export { GremlinResultsTablePath };
